import { useQuery } from '@tanstack/react-query';
import { getTeamCredits } from '@/services/teams-client.service';
import { useSession } from '@descope/nextjs-sdk/client';
import { TeamCredits } from '../types';

export const TEAM_CREDITS_QUERY_KEY = ['teamCredits'] as const;
// Credits are explicitly invalidated after purchase/reward mutations. A
// navigation-length stale window prevents them from repeatedly joining a page
// load's critical requests.
export const TEAM_CREDITS_STALE_TIME_MS = 5 * 60_000;

interface UseTeamCreditsOptions {
  hasTeam?: boolean; // Only query if user has a team
  userTeamsLoading?: boolean; // Wait for team check to complete
}

export const useTeamCredits = (options?: UseTeamCreditsOptions) => {
  const { isAuthenticated, sessionToken } = useSession();
  const { hasTeam = false, userTeamsLoading = false } = options || {};

  // Only query team credits if we know the user has a team
  // Wait for team check to complete before querying
  const {
    data: teamCredits = { hasTeam: false },
    isLoading,
    refetch: refetchTeamCredits,
    isFetching
  } = useQuery<TeamCredits>({
    queryKey: TEAM_CREDITS_QUERY_KEY,
    queryFn: async () => {
      if (!sessionToken) {
        return { hasTeam: false };
      }
      return await getTeamCredits(sessionToken);
    },
    enabled: isAuthenticated && !!sessionToken && hasTeam && !userTeamsLoading,
    staleTime: TEAM_CREDITS_STALE_TIME_MS,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false, // Prevent flickering
    placeholderData: (previousData) => previousData, // Prevent flickering
  });

  const refreshTeamCredits = async () => {
    await refetchTeamCredits();
  };

  return {
    teamCredits,
    isLoading: isLoading && (!teamCredits || teamCredits.hasTeam === false),
    isFetching,
    refetchTeamCredits,
    refreshTeamCredits
  };
};
